/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/export */
import React from "react";
import {HOC} from "./types";

/**
 * composeHOCs
 *
 * Composes single-argument HOCs from right to left.
 *
 * @param HOCs The HOCs to composeHOCs.
 * @returns An HOC obtained by composing the argument HOCs from right
 *   to left. For example, `composeHOCs(withF, withG, withH)` is identical to doing
 *   `(Component) => withF(withG(withH(Component)))`.
 */
export default function composeHOCs(): <
  InputProps extends Record<string, unknown>
>(
  Component: React.ComponentType<InputProps>
) => React.ComponentType<InputProps>;

export default function composeHOCs<
  InputProps extends Record<string, unknown>,
  OutputProps extends Record<string, unknown>
>(withHOC: HOC<InputProps, OutputProps>): HOC<InputProps, OutputProps>;

/* two functions */
export default function composeHOCs<
  InputProps extends Record<string, unknown>,
  IntermediateProps extends Record<string, unknown>,
  OutputProps extends Record<string, unknown>
>(
  withHOC1: HOC<IntermediateProps, OutputProps>,
  withHOC2: HOC<InputProps, IntermediateProps>
): HOC<InputProps, OutputProps>;

/* three functions */
export default function composeHOCs<
  InputProps extends Record<string, unknown>,
  IntermediateProps1 extends Record<string, unknown>,
  IntermediateProps2 extends Record<string, unknown>,
  OutputProps extends Record<string, unknown>
>(
  withHOC1: HOC<IntermediateProps2, OutputProps>,
  withHOC2: HOC<IntermediateProps1, IntermediateProps2>,
  withHOC3: HOC<InputProps, IntermediateProps1>
): HOC<InputProps, OutputProps>;

/* four functions */
export default function composeHOCs<
  InputProps extends Record<string, unknown>,
  IntermediateProps1 extends Record<string, unknown>,
  IntermediateProps2 extends Record<string, unknown>,
  IntermediateProps3 extends Record<string, unknown>,
  OutputProps extends Record<string, unknown>
>(
  withHOC1: HOC<IntermediateProps3, OutputProps>,
  withHOC2: HOC<IntermediateProps2, IntermediateProps3>,
  withHOC3: HOC<IntermediateProps1, IntermediateProps2>,
  withHOC4: HOC<InputProps, IntermediateProps1>
): HOC<InputProps, OutputProps>;

/* rest */
export default function composeHOCs<GenericHOC>(
  f1: (a: React.ComponentType<any>) => GenericHOC,
  ...funcs: Function[]
): GenericHOC;

export default function composeHOCs<GenericHOC>(
  ...funcs: Function[]
): GenericHOC;

export default function composeHOCs(...HOCs: Function[]): Function {
  if (HOCs.length === 0) {
    return <InputProps>(Component: React.ComponentType<InputProps>) =>
      Component;
  }

  if (HOCs.length === 1) {
    return HOCs[0];
  }

  return HOCs.reduce(
    (composedHOC, currentHOC) => (Component: React.ComponentType<any>) =>
      composedHOC(currentHOC(Component))
  );
}
